import { Component, OnInit, ViewEncapsulation, ViewChild, Input } from '@angular/core';

import { Router } from '@angular/router';
import { DialogService, NotificationService, TranslationService } from '../../shared/index';
import { NavigationService } from '../../shared/navigation/navigation.service';

import { DashboardService } from '../shared/dashboard.service';
import { MembershipActivityRow } from '../shared/membership-activity-row.model';
import { DashboardRetentionComponent } from './retention.component';
import { MenuItem } from 'primeng/api';

@Component({
  selector: 'wl-dashboard-membership-activity',
  templateUrl: 'membership-activity.component.html',
  encapsulation: ViewEncapsulation.None,
  providers: [DashboardService],
  styleUrls: ['membership-activity.component.less'],
    standalone: false
})
export class DashboardMembershipActivityComponent implements OnInit {
  private _router: Router;
  private _notificationService: NotificationService;
  private _dialogService: DialogService;
  private _navigationService: NavigationService;
  private _dashboardService: DashboardService;

  public showMembershipActivityDetails: boolean;
  public membershipActivityDetails: Array<MembershipActivityRow>;
  public membershipActivityTotals: MembershipActivityRow;
  public startDate: Date;
  public endDate: Date;
  public isLoading: boolean;
  public engagementReportsItems: MenuItem[];
  public selectedEngagementReport: MenuItem;

  public showContextBusyIndicator: boolean;

  @Input() hideSalesOpps: boolean;
  @Input() hideTasks: boolean;
  @ViewChild('retentionReport', { static: true }) retentionComponent: DashboardRetentionComponent;

  public constructor(
    router: Router,
    notificationService: NotificationService,
    dialogService: DialogService,
    navigationService: NavigationService,
    dashboardService: DashboardService,
    private _translationService: TranslationService
  ) {
    this._router = router;
    this._notificationService = notificationService;
    this._dialogService = dialogService;
    this._navigationService = navigationService;
    this._dashboardService = dashboardService;

    this.isLoading = true;
    this.membershipActivityDetails = new Array<MembershipActivityRow>();
    this.membershipActivityTotals = new MembershipActivityRow();
  }

  async ngOnInit() {
    this.showContextBusyIndicator = false;
    this.showMembershipActivityDetails = false;

    this.startDate = new Date(new Date().getFullYear(), 0, 1);
    this.endDate = new Date();

    this.retentionComponent.startDate = this.startDate;
    this.retentionComponent.endDate = this.endDate;

    this.getMembershipActivityReport();
    this.retentionComponent.getRetentionReport();

    const eventParticipationEngagementLabel = await this._translationService.getTitlecase(
      'dashboard.membership-activity.reports.event-participation'
    );
    const contactRecordEngagementLabel = await this._translationService.getTitlecase(
      'dashboard.membership-activity.reports.contact-record-engagement'
    );
    const nonDuesEngagementLabel = await this._translationService.getTitlecase(
      'dashboard.membership-activity.reports.non-dues-revenue-engagement'
    );
    const referralRateEngagementLabel = await this._translationService.getTitlecase(
      'dashboard.membership-activity.reports.referral-rate-engagement'
    );
    const committeeParticipationLabel = await this._translationService.getTitlecase(
      'dashboard.membership-activity.reports.committee-participation'
    );

    this.engagementReportsItems = [
      {
        id: 'member-activity-event-participation-engagement-report-link',
        label: eventParticipationEngagementLabel,
        command: (event) => {
          this._navigationService.navigateToEventParticipationEngagementReport();
        },
      },
      {
        id: 'member-activity-contactRecord-engagement-report-link',
        label: contactRecordEngagementLabel,
        command: (event) => {
          this._navigationService.navigateToContactEngagementReport();
        },
      },
      {
        id: 'member-activity-non-dues-engagement-report-link',
        label: nonDuesEngagementLabel,
        command: (event) => {
          this._navigationService.navigateToNonDuesRevenueEngagementReport();
        },
      },
      {
        id: 'member-activity-referral-rate-engagement-report-link',
        label: referralRateEngagementLabel,
        command: (event) => {
          this._navigationService.navigateToReferralRateEngagementReport();
        },
      },
      {
        id: 'member-activity-committee-participation-engagement-report-link',
        label: committeeParticipationLabel,
        command: (event) => {
          this._navigationService.navigateToCommitteeParticipationEngagementReport();
        },
      },
    ];
  }

  public renewalStatistics2021Clicked() {
    this._navigationService.navigateRenwalStatisticsReport();
  }

  public toggleMembershipActivityDetails(): void {
    this.showMembershipActivityDetails = !this.showMembershipActivityDetails;
  }

  public selectedDateChange(): void {
    this.retentionComponent.startDate = this.startDate;
    this.retentionComponent.endDate = this.endDate;

    this.getMembershipActivityReport();
    this.retentionComponent.getRetentionReport();
  }

  public getMembershipActivityReport() {
    this._dashboardService.getMembershipActivityReport(this.startDate, this.endDate).then((data: Array<MembershipActivityRow>) => {
      this.membershipActivityDetails = data;

      this.calculateMembershipActivityTotals();
      this.isLoading = false;
    });
  }

  public openMembersJoinedReport() {
    this._navigationService.navigateToMembersJoinedReport();
  }

  public openMemberRetentionReport() {
    this._navigationService.navigateToMemberRetentionReport();
  }

  public openMembersDroppedReport() {
    this._navigationService.navigateToMembersDroppedReport();
  }

  private calculateMembershipActivityTotals(): void {
    this.membershipActivityTotals = new MembershipActivityRow();

    let newMembers: number = this.membershipActivityDetails.reduce((newMembers, current) => newMembers + current.NewMembers, 0);
    this.membershipActivityTotals.NewMembers = newMembers;

    let newInvestment: number = this.membershipActivityDetails.reduce((newInvestment, current) => newInvestment + current.NewInvestment, 0);
    this.membershipActivityTotals.NewInvestment = newInvestment;

    let droppedMembers: number = this.membershipActivityDetails.reduce(
      (droppedMembers, current) => droppedMembers + current.DroppedMembers,
      0
    );
    this.membershipActivityTotals.DroppedMembers = droppedMembers;

    let lostInvestment: number = this.membershipActivityDetails.reduce(
      (lostInvestment, current) => lostInvestment + current.LostInvestment,
      0
    );
    this.membershipActivityTotals.LostInvestment = lostInvestment;

    let gainLoss: number = this.membershipActivityDetails.reduce((gainLoss, current) => gainLoss + current.GainLoss, 0);
    this.membershipActivityTotals.GainLoss = gainLoss;

    let gainLossInvestment: number = this.membershipActivityDetails.reduce(
      (gainLossInvestment, current) => gainLossInvestment + current.GainLossInvestment,
      0
    );
    this.membershipActivityTotals.GainLossInvestment = gainLossInvestment;

    let totalMembers: number = this.membershipActivityDetails.reduce((totalMembers, current) => totalMembers + current.TotalMembers, 0);
    this.membershipActivityTotals.TotalMembers = totalMembers;
  }
}
